import { useContext, useEffect, useState } from "react";
import { Receipt, User } from "lucide-react";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import BoxAlert from "../../components/boxAlert";
import Loader from "../../components/loader";
import ClientDataContext from "../../contexts/clientContext";
import CompanyContext from "../../contexts/companyContext";
import '../../css/scrollbar.css';
import useStorage from "../../hooks/useStorage";
import { formatValue } from "../../scripts/formatValue";
import axios from "../../services/axios";
import RenderOrders from "./components/renderOrders";
import styles from "./css/account.module.css";


function Account() {
  const storage = useStorage();
  const {companyData} = useContext(CompanyContext);

  const {clientData, setClientData} = useContext(ClientDataContext);
  
  const [orders, setOrders] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [event, setEvent] = useState({using: false, price: null});
  const [subtotalValue, setSubtotalValue] = useState(0);
  const [tax, setTax] = useState(0);

  const [expandOrders, setExpandOrders] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // LOADING CONTROLLER
  const [loading, setLoading] = useState(true);

  // WARN CONTROL
  const [warnAlert, setWarnAlert] = useState({show: false, title: null, text: null});




  useEffect(() => {
    (async () => {
      if (clientData && clientData.number) {
        await testCommand();
        await getOrders();
        
      } else if (companyData.type == 'delivery') {
        await getOrders();
        

      } else {
        setLoading(false);
      }
    })();
  }, []);



  // TEST COMMAND FUNCTION ============================>
  const testCommand = async () => {
    try {

      const res = await axios.get(
        `/smartmenu/command/${clientData.id}`,
        {
          params: {
            companyId: companyData.id,
            number: clientData.number,
            tax: companyData.service_tax,
          },
        }
      );

      if (res.status === 200) {

        if (!res.data?.status) {
          storage.exclude("CLIENT_DATA");

          setClientData([]);
          
        } else {

          if(companyData.type !== 'delivery') {
            setEvent({using: true, price: formatValue(res.data.price.event ?? 0)});

            setSubtotalValue(formatValue(res.data.price.subtotal));

            setTax(formatValue(res.data.price.service_tax));

            setTotalPrice(formatValue(res.data.price.total));
          }

          // Atualiza o número da comanda física vinculada, caso o operador tenha
          // vinculado/desvinculado um cartão físico após o registro inicial da comanda.
          setClientData((prev) => ({ ...prev, physical_number: res.data.physical_number ?? null }));


        }
      }
      
    } catch (error) {
      console.error('ERROR - TEST COMMAND FUNCTION:', error);
    }
  };


  // GET CLIENT ORDERS FUNCTION
  const getOrders = async () => {
    try {
      let sessionUUID = null;
      
      if(companyData.type == 'delivery'){
        const deliverySession = storage.get('DELIVERY_ACCOUNT_SESSION');

        sessionUUID = deliverySession?.sessionUUID ?? null;

        if(!sessionUUID){
          return;
        }

      } else {
        sessionUUID = clientData?.sessionUUID ?? null;

        if(!sessionUUID){
          return;
        }
      }
      
      const url = `/smartmenu/${companyData.type !== 'delivery' ? 'client/orders' : 'client/orders/delivery'}`;

      const res = await axios.get(url, {
        params: {
          companyId: companyData.id,
        },
        headers: {
          'x-session-uuid': sessionUUID,
        },
      });

      if (res.status === 200 && res.data) {
        if(companyData.type == 'delivery'){
          // CONTROLA SOMA DE VALOR TOTAL DA CONTA DO CLIENTE
          const soma = res.data.reduce((total, item) => total + parseFloat(item.total ?? item.price ?? 0), 0);
          
          setTotalPrice(formatValue(soma));
        }

        const arr = [...res.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(arr);
        setLastUpdate(new Date());

      } else {
        setExpandOrders(true);
      }

    } catch (error) {
      console.error("ERROR - GETTING USER ORDERS: ", error);

      if (error?.response?.status === 401) {
        if (companyData.type === 'delivery') {
          storage.exclude('DELIVERY_ACCOUNT_SESSION');

        } else {
          storage.exclude('CLIENT_DATA');
          setClientData([]);
        }
      }

      setWarnAlert({show: true, title: 'Erro', text: 'Não foi possível encontrar seus pedidos. Tente novamente em instantes!'});

    } finally {
      setLoading(false);
    }
  };


  // AUTO UPDATE ORDERS - Interval com Page Visibility API
  useEffect(() => {
    // Só ativa o interval se tiver dados para atualizar
    const shouldAutoUpdate = 
      (companyData.type !== 'delivery' && clientData && clientData.id) || 
      (companyData.type === 'delivery' && orders.length > 0);

    if (!shouldAutoUpdate) return;

    let intervalId = null;

    // Função para atualizar pedidos
    const updateOrders = async () => {
      try {
        await getOrders();
      } catch (error) {
        console.error('ERROR - AUTO UPDATE ORDERS:', error);
      }
    };

    // Inicia o interval
    const startInterval = () => {
      if (!intervalId) {
        intervalId = setInterval(updateOrders, process.env.REACT_APP_ORDERS_INTERVAL_TIME);
      }
    };

    // Para o interval
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Listener para Page Visibility API
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        startInterval();
        // Atualiza imediatamente quando volta para a aba
        updateOrders();
      }
    };

    // Adiciona listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Inicia o interval
    startInterval();

    // Cleanup: remove listener e para o interval
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clientData?.id, companyData.type, companyData.id, orders.length]);




  // SCREEN =========================>
  return (
    <>
      {loading ? (
        <Loader />

      ) : (
        <div className={styles.screen} style={{'--brand': companyData.background_color, '--on-brand': companyData.text_color}}>


          <section className={styles.headerAccount} style={{ backgroundColor: companyData.background_color }}>
            <div className={styles.headerCartTextArea}>
              <h1 className={styles.accountTitle} style={{ color: companyData.text_color }}>Conta</h1>
              <p className={styles.accountSubtitle} style={{ color: companyData.text_color }}>
                Verifique seus pedidos e valores
              </p>
            </div>
          </section>

          <div id={styles.container}>
            {(companyData.type !== 'delivery' && clientData && clientData.id) || (companyData.type == 'delivery' && orders.length > 0)  ? (
              <>
                {companyData.type !== 'delivery' && clientData && clientData.id ? (
                  <section className={styles.dataArea}>
                    <p className={styles.sectionTitle}>
                      <User size={16} />
                      Seus dados
                    </p>

                    <div className={styles.dataGrid}>
                      <div className={styles.dataItem}>
                        <span className={styles.dataLabel}>Comanda</span>
                        <span className={styles.dataValue}>{clientData.number}</span>
                      </div>

                      <div className={styles.dataItem}>
                        <span className={styles.dataLabel}>{clientData.location == 0 ? 'Local' : 'Mesa'}</span>
                        <span className={styles.tableBadge}>{clientData.location == 0 ? 'Balcão' : clientData.location}</span>
                      </div>

                      {clientData.physical_number ? (
                        <div className={styles.dataItem}>
                          <span className={styles.dataLabel}>Comanda física</span>
                          <span className={styles.dataValue}>{clientData.physical_number}</span>
                        </div>
                      ) : null}

                      <div className={styles.dataItem}>
                        <span className={styles.dataLabel}>Cliente</span>
                        <span className={styles.dataValue}>{clientData.client_name || 'N/A'}</span>
                      </div>

                      {clientData.client_phone ? (
                        <div className={styles.dataItem}>
                          <span className={styles.dataLabel}>Telefone</span>
                          <span className={styles.dataValue}>{clientData.client_phone}</span>
                        </div>
                      ) : null}
                    </div>
                  </section>

                ) : null}


                {/* ÁREA DOS PEDIDOS DO CLIENTE */}
                <div className={styles.ordersArea}>
                  <div className={styles.topOrdersArea}>
                    <h2 className={styles.ordersTitle}>
                      Pedidos
                      {orders.length > 0 && <span className={styles.ordersCount}>{orders.length}</span>}
                    </h2>

                    <button
                      className={styles.expandOrdersListAccount}
                      aria-label={expandOrders ? 'Recolher pedidos' : 'Expandir pedidos'}
                      onClick={() => setExpandOrders((prev) => !prev)}
                    >
                      {expandOrders ? <BiChevronUp size={26} color="#333" /> : <BiChevronDown size={26} color="#333" />}
                    </button>
                  </div>

                  {/* {lastUpdate && (
                    <p style={{fontSize: 10, color: '#666', textAlign: 'center', marginBottom: 5}}>
                      Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
                    </p>
                  )} */}

                  {orders && orders.length > 0 && expandOrders ? (
                    <ul className={styles.ordersList}>
                      {orders.map((item, index) => 
                        <RenderOrders 
                          key={item.id ?? item.code ?? index}
                          item={item} 
                          index={index} 
                          companyData={companyData}
                        />
                      )}
                    </ul>

                  ) : orders && orders.length == 0 ? (
                    <p className={styles.noOrders}>
                      Nenhum pedido encontrado!
                    </p>
                  ) : null}


                  <div className={styles.totalsArea}>
                    {companyData.type !== 'delivery' ? (
                      <>
                        <div className={styles.totalRow}>
                          <span>Subtotal</span>
                          <span>{subtotalValue}</span>
                        </div>

                        <div className={styles.totalRow}>
                          <span>Taxa de serviço</span>
                          <span>{tax}</span>
                        </div>

                        <div className={styles.totalRow}>
                          <span>Evento</span>
                          <span>{event.price}</span>
                        </div>
                      </>

                    ) : null}

                    <div className={`${styles.totalRow} ${styles.grand}`}>
                      <span>Total</span>
                      <span>{totalPrice}</span>
                    </div>
                  </div>

                </div>

              </>

            ) : (

              <div className={styles.notLoggedAlert}>
                <div className={styles.emptyIcon}>
                  <Receipt size={34} />
                </div>

                {companyData.type == 'delivery' ? (
                  <>
                    <h2 className={styles.titleNotLoggedAlert}>Sem dados de pedidos</h2>

                    <p className={styles.subtextNotLoggedAlert}>
                      Realize um pedido, insira seus dados de entrega e tudo aparecerá aqui.
                    </p>
                  </>
                  
                ) : ( 
                  <>
                    <h2 className={styles.titleNotLoggedAlert}>Sem dados de comanda</h2>

                    <p className={styles.subtextNotLoggedAlert}>
                      Realize um pedido para que nós possamos gerar sua comanda.
                      <strong>
                        Após isso, todos os dados dos seus pedidos e conta serão
                        apresentados aqui!
                      </strong>
                    </p>
                  </>
                )}

              </div>

            )}

          </div>
        </div>
      )}

      <BoxAlert
        show={warnAlert.show}
        title={warnAlert.title}
        text={warnAlert.text}
        onClose={() => setWarnAlert({show: false, title: null, text: null})}
      />
    </>
  );
}

export default Account;
